import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';

const ParkingAssignUnitForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        siteId: "",
        wingId: "",
        floorId: "",
        unitId: "",
        parkingCategoryId: "",
        vehicleCategoryId: "",
        parkingSlotId: ""
    });

    const [siteList, setSiteList] = useState([]);
    const [wingList, setWingList] = useState([]);
    const [floorList, setFloorList] = useState([]);
    const [unitList, setUnitList] = useState([]);
    const [vehicleTypeList, setVehicleTypeList] = useState([]);
    const [parkingTypeList, setParkingTypeList] = useState([]);
    const [parkingSlotList, setParkingSlotList] = useState([]);

    const isEdit = !!location.state?.recordData;
    const recordData = location.state?.recordData;

    useEffect(() => {
        if (isEdit && recordData) {
            setFormData({
                siteId: recordData.siteId || "",
                wingId: recordData.wingId || "",
                floorId: recordData.floorId || "",
                unitId: recordData.unitId || "",
                parkingCategoryId: recordData.parkingCategoryId || "",
                vehicleCategoryId: recordData.vehicleCategoryId || "",
                parkingSlotId: recordData.parkingSlotId || ""
            });
            if (recordData.siteId) fetchWings(recordData.siteId);
            if (recordData.wingId) fetchFloors(recordData.wingId);
            if (recordData.floorId) fetchUnits(recordData.floorId);
        }
        fetchDropdownData();
    }, [isEdit, recordData]);

    const extractArray = (apiData, fallbackKey) => {
        if (!apiData) return [];
        if (Array.isArray(apiData)) return apiData;
        if (apiData.data && Array.isArray(apiData.data)) return apiData.data;
        if (fallbackKey && apiData[fallbackKey] && Array.isArray(apiData[fallbackKey])) return apiData[fallbackKey];
        return [];
    };

    const fetchDropdownData = async () => {
        try {
            const siteRes = await apiServices({ method: "GET", url: `/SiteDetailsMaster/GetAllSiteDetails` });
            if (siteRes.isSuccess || siteRes.success || siteRes.IsSuccess) setSiteList(extractArray(siteRes.data, 'SiteDetailsData').map(s => ({label: s.siteName, value: s.siteId})));
            
            const vehicleRes = await apiServices({ method: "GET", url: `/VehicleTypeMaster/getAllVehicleType` });
            if (vehicleRes.isSuccess || vehicleRes.success || vehicleRes.IsSuccess) setVehicleTypeList(extractArray(vehicleRes.data, 'VehicleTypeData').map(v => ({label: v.VehicleTypename, value: v.VehicleTypeid})));
            
            const parkingRes = await apiServices({ method: "GET", url: `/ParkingTypeMaster/GetAllParkingTypes` });
            if (parkingRes.isSuccess || parkingRes.success || parkingRes.IsSuccess) {
                const pTypes = extractArray(parkingRes.data, 'ParkingTypeData');
                setParkingTypeList(pTypes.filter(item => item.IsActive === 'Active' || item.isActive === 'Active' || item.IsActive === 1).map(p => ({label: p.parkingType, value: p.parkingTypeID})));
            }

            const slotRes = await apiServices({ method: "GET", url: `/ParkingSlotMaster/getallParkingSlotId` });
            if (slotRes.isSuccess || slotRes.success || slotRes.IsSuccess) {
                const slots = extractArray(slotRes.data, 'ParkingSlotData');
                setParkingSlotList(slots.filter(item => item.isOccupied === 0).map(s => ({label: `${s.slotName} - ${s.floorName}`, value: s.parkingSlotId})));
            }
            
        } catch (error) {
            console.error("Error fetching dropdowns:", error);
            toast.error("Failed to load form options");
        }
    };

    const fetchWings = async (siteId) => {
        try {
            const res = await apiServices({ method: "GET", url: `/WingMaster/GetAllWing` });
            if (res.isSuccess || res.success || res.IsSuccess) {
                const wings = extractArray(res.data, 'WingMasterData');
                setWingList(wings.filter(item => item.SiteId === parseInt(siteId)).map(w => ({label: w.WingName, value: w.WingId})));
            }
        } catch (error) { console.error(error); }
    };

    const fetchFloors = async (wingId) => {
        try {
            const res = await apiServices({ method: "GET", url: `/FloorMaster/GetAllFloor` });
            if (res.isSuccess || res.success || res.IsSuccess) {
                const floors = extractArray(res.data, 'FloorMasterData');
                setFloorList(floors.filter(item => item.WingId === parseInt(wingId)).map(f => ({label: f.FloorName, value: f.FloorId})));
            }
        } catch (error) { console.error(error); }
    };

    const fetchUnits = async (floorId) => {
        try {
            const res = await apiServices({ method: "GET", url: `/UnitMaster/GetAllUnitMaster` });
            if (res.isSuccess || res.success || res.IsSuccess) {
                const units = extractArray(res.data, 'UnitMasterData');
                setUnitList(units.filter(item => item.FloorId === parseInt(floorId)).map(u => ({label: u.UnitName, value: u.UnitId})));
            }
        } catch (error) { console.error(error); }
    };

    const handleSiteChange = (opt) => {
        const siteId = opt ? opt.value : "";
        setFormData({ ...formData, siteId, wingId: "", floorId: "", unitId: "" });
        setWingList([]);
        setFloorList([]);
        setUnitList([]);
        if(siteId) fetchWings(siteId);
    };

    const handleWingChange = (opt) => {
        const wingId = opt ? opt.value : "";
        setFormData({ ...formData, wingId, floorId: "", unitId: "" });
        setFloorList([]);
        setUnitList([]);
        if(wingId) fetchFloors(wingId);
    };

    const handleFloorChange = (opt) => {
        const floorId = opt ? opt.value : "";
        setFormData({ ...formData, floorId, unitId: "" });
        setUnitList([]);
        if(floorId) fetchUnits(floorId);
    };

    const validateForm = () => {
        if (!formData.siteId) return toast.warning("Site is required"), false;
        if (!formData.wingId) return toast.warning("Wing is required"), false;
        if (!formData.floorId) return toast.warning("Floor is required"), false;
        if (!formData.unitId) return toast.warning("Unit Name is required"), false;
        if (!formData.parkingCategoryId) return toast.warning("Parking Category is required"), false;
        if (!formData.vehicleCategoryId) return toast.warning("Vehicle Category is required"), false;
        if (!formData.parkingSlotId) return toast.warning("Parking Slot is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            unitId: parseInt(formData.unitId),
            slotId: parseInt(formData.parkingSlotId)
        };

        try {
            const res = await apiServices({
                method: "POST",
                url: `/ParkingSlotMaster/AssignParmanentSlotToUnit`,
                data: payload
            });

            if (res.isSuccess || res.success || res.IsSuccess) {
                toast.success(`Parking slot assigned to unit successfully.`);
                navigate('/zentcomm/parking-management/parking-assing-unit');
            } else {
                toast.error(res.message || `Failed to assign parking slot to unit.`);
            }
        } catch (error) {
            toast.error("An error occurred while assigning slot");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <PageHeadingSection 
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Assign'} Parking Slot`}
                subHeading={`Please fill out the form below to assign a permanent parking slot to a specific unit.`}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <CustomSelect 
                    label={<>Site <span className="text-red-500">*</span></>}
                    options={siteList}
                    value={siteList.find(s => s.value === formData.siteId) || null}
                    onChange={handleSiteChange}
                />
                
                <CustomSelect 
                    label={<>Wing <span className="text-red-500">*</span></>}
                    options={wingList}
                    value={wingList.find(w => w.value === formData.wingId) || null}
                    onChange={handleWingChange}
                    isDisabled={!formData.siteId}
                />
                
                <CustomSelect 
                    label={<>Floor Name <span className="text-red-500">*</span></>}
                    options={floorList}
                    value={floorList.find(f => f.value === formData.floorId) || null}
                    onChange={handleFloorChange}
                    isDisabled={!formData.wingId}
                />
                
                <CustomSelect 
                    label={<>Unit Name <span className="text-red-500">*</span></>}
                    options={unitList}
                    value={unitList.find(u => u.value === formData.unitId) || null}
                    onChange={(opt) => setFormData({ ...formData, unitId: opt ? opt.value : "" })}
                    isDisabled={!formData.floorId}
                />
                
                <CustomSelect 
                    label={<>Parking Category <span className="text-red-500">*</span></>}
                    options={parkingTypeList}
                    value={parkingTypeList.find(p => p.value === formData.parkingCategoryId) || null}
                    onChange={(opt) => setFormData({ ...formData, parkingCategoryId: opt ? opt.value : "" })}
                />
                
                <CustomSelect 
                    label={<>Vehicle Category <span className="text-red-500">*</span></>}
                    options={vehicleTypeList}
                    value={vehicleTypeList.find(v => v.value === formData.vehicleCategoryId) || null}
                    onChange={(opt) => setFormData({ ...formData, vehicleCategoryId: opt ? opt.value : "" })}
                />
                
                <CustomSelect 
                    label={<>Parking Slot <span className="text-red-500">*</span></>}
                    options={parkingSlotList}
                    value={parkingSlotList.find(s => s.value === formData.parkingSlotId) || null}
                    onChange={(opt) => setFormData({ ...formData, parkingSlotId: opt ? opt.value : "" })}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentcomm/parking-management/parking-assing-unit")}
                    className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Assign Parking Slot'}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default ParkingAssignUnitForm;
